import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { Loader2, AlertCircle } from 'lucide-react';
import { cn } from '@utils/cn';

/**
 * Input variants using class-variance-authority
 */
const inputVariants = cva(
  'flex w-full rounded-md border bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:bg-muted disabled:text-muted-foreground disabled:border-muted transition-colors',
  {
    variants: {
      inputSize: {
        sm: 'h-9 text-sm',
        md: 'h-10 text-sm',
        lg: 'h-11 text-base',
      },
      state: {
        default: 'border-input',
        error: 'border-destructive focus-visible:ring-destructive',
        success: 'border-success focus-visible:ring-success',
      },
    },
    defaultVariants: {
      inputSize: 'md',
      state: 'default',
    },
  }
);

export interface InputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'>,
    VariantProps<typeof inputVariants> {
  /**
   * Display loading spinner
   */
  loading?: boolean;
  /**
   * Error message to display
   */
  error?: string;
  /**
   * Success message to display
   */
  success?: string;
  /**
   * Helper text to display below the input
   */
  helperText?: string;
  /**
   * Icon to display on the left side
   */
  leftIcon?: React.ReactNode;
  /**
   * Icon to display on the right side
   */
  rightIcon?: React.ReactNode;
  /**
   * Label for the input
   */
  label?: string;
  /**
   * Wrapper div className
   */
  wrapperClassName?: string;
  /**
   * Mark field as required. Displays asterisk next to label.
   */
  required?: boolean;
}

/**
 * Input Component
 *
 * A flexible input component with support for various states, icons, and helper text.
 * Fully integrated with React Hook Form for form validation.
 *
 * @example
 * // Basic input
 * <Input placeholder="Enter patient name" />
 *
 * @example
 * // Input with label and helper text
 * <Input
 *   label="Patient ID"
 *   placeholder="Enter ID"
 *   helperText="Unique identifier for the patient"
 * />
 *
 * @example
 * // Input with error state
 * <Input
 *   label="Email"
 *   type="email"
 *   error="Invalid email address"
 * />
 *
 * @example
 * // Input with loading state
 * <Input
 *   placeholder="Searching..."
 *   loading={isSearching}
 * />
 *
 * @example
 * // Input with icons
 * <Input
 *   leftIcon={<SearchIcon className="h-4 w-4" />}
 *   placeholder="Search patients..."
 * />
 *
 * @example
 * // With React Hook Form
 * const { register, formState: { errors } } = useForm();
 * <Input
 *   {...register('patientName', { required: 'Name is required' })}
 *   error={errors.patientName?.message}
 *   label="Patient Name"
 * />
 */
const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      inputSize,
      type = 'text',
      loading = false,
      error,
      success,
      helperText,
      leftIcon,
      rightIcon,
      label,
      wrapperClassName,
      disabled,
      id,
      required,
      ...props
    },
    ref
  ) => {
    // Generate a unique ID if not provided
    const generatedId = React.useId();
    const inputId = id || generatedId;
    const errorId = `${inputId}-error`;
    const helperId = `${inputId}-helper`;

    // Determine state based on error/success
    const state = error ? 'error' : success ? 'success' : 'default';

    // Determine right icon based on state
    const displayRightIcon = React.useMemo(() => {
      if (loading) {
        return (
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" aria-hidden="true" />
        );
      }
      if (error) {
        return <AlertCircle className="h-4 w-4 text-destructive" aria-hidden="true" />;
      }
      return rightIcon;
    }, [loading, error, rightIcon]);

    const hasLeftAddon = !!leftIcon;
    const hasRightAddon = !!displayRightIcon;

    return (
      <div className={cn('w-full', wrapperClassName)}>
        {label && (
          <label
            htmlFor={inputId}
            className="mb-2 block text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
          >
            {label}
            {required && (
              <span className="ml-1 text-destructive" aria-label="required">
                *
              </span>
            )}
          </label>
        )}
        <div className="relative">
          {hasLeftAddon && (
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
              {leftIcon}
            </div>
          )}
          <input
            type={type}
            className={cn(
              inputVariants({ inputSize, state }),
              hasLeftAddon && 'pl-10',
              hasRightAddon && 'pr-10',
              className
            )}
            ref={ref}
            disabled={disabled || loading}
            id={inputId}
            aria-invalid={!!error}
            aria-describedby={error ? errorId : helperText || success ? helperId : undefined}
            aria-required={required}
            aria-busy={loading}
            {...props}
          />
          {hasRightAddon && (
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
              {displayRightIcon}
            </div>
          )}
          {loading && <span className="sr-only">Loading...</span>}
        </div>
        {(error || success || helperText) && (
          <div
            id={error ? errorId : helperId}
            className={cn('mt-1.5 text-sm', {
              'text-destructive': error,
              'text-success': success,
              'text-muted-foreground': helperText && !error && !success,
            })}
            role={error ? 'alert' : 'status'}
            aria-live="polite"
          >
            {error || success || helperText}
          </div>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';

export { Input, inputVariants };
